import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAppState } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import * as couponService from '../services/couponService';
import * as customerCouponService from '../services/customerCouponService';
import * as salesService from '../services/salesService';
import { addActivity } from '../services/activityService';
import { creditBillCouponNote } from '../components/SaleAnnotations';
import { getCouponValue } from '../utils/couponSaleSplit';

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/** Sum of credit sales for a customer in the calendar month of dateStr (YYYY-MM-DD). */
function getOutstandingCredit(sales, customerId, dateStr) {
  if (!customerId || !dateStr) return 0;
  const ym = String(dateStr).slice(0, 7);
  return roundMoney(
    (sales || [])
      .filter((s) => s.customerId === customerId)
      .filter((s) => String(s.date || '').slice(0, 7) === ym)
      .filter((s) => {
        const t = s.paymentType;
        if (t === 'cash' || t === 'coupon') return false;
        if (s.couponId || s.couponName) return false;
        return true;
      })
      .reduce((sum, s) => sum + (Number(s.totalAmount) || 0), 0)
  );
}

function Coupons() {
  const { coupons, customers, customerCoupons, sales, refreshData } = useAppState();
  const { currentUser, tenant, couponsEnabled, setCouponsFeatureEnabled, canManageUsers } =
    useAuth();
  const [tab, setTab] = useState(0);
  const [togglingFeature, setTogglingFeature] = useState(false);

  // ── Coupon types ──
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    quantity: '1',
    rate: '',
    isActive: true,
  });

  // ── Wallet list filters ──
  const [walletDept, setWalletDept] = useState('');
  const [walletSearch, setWalletSearch] = useState('');
  const [walletSelected, setWalletSelected] = useState([]);
  const [billMenuAnchor, setBillMenuAnchor] = useState(null);
  const [billMenuEntries, setBillMenuEntries] = useState([]);
  const [billMenuAllEntries, setBillMenuAllEntries] = useState([]);

  // ── Apply to credit bill ──
  const [billOpen, setBillOpen] = useState(false);
  const [billEntry, setBillEntry] = useState(null);
  const [billAmount, setBillAmount] = useState('');
  const [billDate, setBillDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [billSaving, setBillSaving] = useState(false);
  const [billError, setBillError] = useState('');

  const departments = useMemo(() => {
    const set = new Set();
    (customers || []).forEach((c) => {
      const d = (c.department || '').trim();
      if (d) set.add(d);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [customers]);

  const customerById = useMemo(
    () => Object.fromEntries((customers || []).map((c) => [c.id, c])),
    [customers]
  );

  const unusedWallet = useMemo(() => {
    const q = walletSearch.toLowerCase().trim();
    return (customerCoupons || [])
      .filter((cc) => roundMoney(cc.remainingValue) > 0)
      .filter((cc) => {
        const cust = customerById[cc.customerId];
        if (walletDept && (cust?.department || '').trim() !== walletDept) return false;
        if (!q) return true;
        const name = (cust?.name || '').toLowerCase();
        const dept = (cust?.department || '').toLowerCase();
        const couponName = (cc.couponName || '').toLowerCase();
        return name.includes(q) || dept.includes(q) || couponName.includes(q);
      })
      .sort((a, b) => {
        const na = customerById[a.customerId]?.name || '';
        const nb = customerById[b.customerId]?.name || '';
        return na.localeCompare(nb);
      });
  }, [customerCoupons, customerById, walletDept, walletSearch]);

  /** One row per customer with their unused coupon entries grouped. */
  const walletByCustomer = useMemo(() => {
    const map = new Map();
    unusedWallet.forEach((cc) => {
      const key = cc.customerId || cc.id;
      if (!map.has(key)) {
        map.set(key, {
          customerId: cc.customerId,
          entries: [],
          remainingTotal: 0,
          originalTotal: 0,
        });
      }
      const row = map.get(key);
      row.entries.push(cc);
      row.remainingTotal = roundMoney(row.remainingTotal + roundMoney(cc.remainingValue));
      row.originalTotal = roundMoney(row.originalTotal + roundMoney(cc.originalValue));
    });
    return [...map.values()].sort((a, b) => {
      const na = customerById[a.customerId]?.name || '';
      const nb = customerById[b.customerId]?.name || '';
      return na.localeCompare(nb);
    });
  }, [unusedWallet, customerById]);

  function couponTypeKey(cc) {
    if (cc?.couponId) return `id:${cc.couponId}`;
    return `name:${(cc?.couponName || '').trim().toLowerCase()}`;
  }

  /** Group a customer's wallet entries by coupon type for display. */
  function groupEntriesByCouponType(entries) {
    const map = new Map();
    (entries || []).forEach((cc) => {
      const key = couponTypeKey(cc);
      if (!map.has(key)) {
        map.set(key, {
          key,
          couponId: cc.couponId || null,
          couponName: cc.couponName || 'Coupon',
          count: 0,
          remainingValue: 0,
          entries: [],
        });
      }
      const g = map.get(key);
      g.count += 1;
      g.remainingValue = roundMoney(g.remainingValue + roundMoney(cc.remainingValue));
      g.entries.push(cc);
    });
    return [...map.values()].sort((a, b) => a.couponName.localeCompare(b.couponName));
  }

  // ── Coupon type handlers ──
  const handleOpen = (coupon = null) => {
    if (coupon) {
      setEditing(coupon);
      setForm({
        name: coupon.name,
        quantity: String(coupon.quantity ?? 1),
        rate: String(coupon.rate ?? ''),
        isActive: coupon.isActive !== false,
      });
    } else {
      setEditing(null);
      setForm({ name: '', quantity: '1', rate: '', isActive: true });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setForm({ name: '', quantity: '1', rate: '', isActive: true });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    const qty = Number(form.quantity);
    if (!qty || qty <= 0) return;

    const userId = currentUser?.userId || 'Unknown';
    const payload = {
      name: form.name.trim(),
      quantity: qty,
      rate: Number(form.rate) || 0,
      isActive: form.isActive !== false,
    };

    if (editing) {
      await couponService.updateCoupon(editing.id, payload);
      addActivity(userId, `Updated coupon: ${payload.name}`);
    } else {
      await couponService.addCoupon(payload);
      addActivity(userId, `Created coupon: ${payload.name}`);
    }
    refreshData();
    handleClose();
  };

  const handleToggleActive = async (coupon) => {
    const nextActive = coupon.isActive === false;
    setTogglingId(coupon.id);
    try {
      await couponService.updateCoupon(coupon.id, { isActive: nextActive });
      addActivity(
        currentUser?.userId || 'Unknown',
        `${nextActive ? 'Enabled' : 'Disabled'} coupon: ${coupon.name}`
      );
      refreshData();
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await couponService.deleteCoupon(id);
      addActivity(currentUser?.userId || 'Unknown', `Deleted coupon: ${name}`);
      refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to delete coupon from database');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(coupons.map((c) => c.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected coupon(s)?`)) return;
    try {
      for (const id of selected) {
        const c = coupons.find((x) => x.id === id);
        await couponService.deleteCoupon(id);
        addActivity(currentUser?.userId || 'Unknown', `Deleted coupon: ${c?.name || id}`);
      }
      setSelected([]);
      refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to delete coupon(s) from database');
      refreshData();
    }
  };

  const canSave = form.name?.trim() && Number(form.quantity) > 0;

  const handleToggleCouponsFeature = async (nextEnabled) => {
    if (!canManageUsers) {
      window.alert('Only managers can enable or disable coupons for this canteen.');
      return;
    }
    if (!tenant?.id || togglingFeature) return;
    setTogglingFeature(true);
    try {
      await setCouponsFeatureEnabled(nextEnabled);
      addActivity(
        currentUser?.userId || 'Unknown',
        `${nextEnabled ? 'Enabled' : 'Disabled'} coupon features for tenant`
      );
    } catch (err) {
      window.alert(err.message || 'Failed to update coupon feature setting');
    } finally {
      setTogglingFeature(false);
    }
  };

  // ── Wallet handlers ──
  const handleWalletSelectAll = (e) => {
    if (e.target.checked) {
      setWalletSelected(unusedWallet.map((c) => c.id));
    } else {
      setWalletSelected([]);
    }
  };

  const handleWalletSelectCustomer = (entryIds) => {
    const idSet = new Set(entryIds);
    const allSelected = entryIds.every((id) => walletSelected.includes(id));
    setWalletSelected((prev) => {
      if (allSelected) return prev.filter((id) => !idSet.has(id));
      const merged = new Set([...prev, ...entryIds]);
      return [...merged];
    });
  };

  const handleDeleteCustomerWallets = async (row) => {
    const cust = customerById[row.customerId];
    const name = cust?.name || 'this customer';
    const count = row.entries.length;
    if (
      !window.confirm(
        `Remove ${count} unused coupon${count === 1 ? '' : 's'} for ${name}?`
      )
    ) {
      return;
    }
    try {
      for (const entry of row.entries) {
        await customerCouponService.deleteCustomerCoupon(entry.id);
      }
      addActivity(
        currentUser?.userId || 'Unknown',
        `Removed ${count} unused coupon(s) for ${name}`
      );
      const removedIds = new Set(row.entries.map((e) => e.id));
      setWalletSelected((prev) => prev.filter((id) => !removedIds.has(id)));
      refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to remove coupons');
      refreshData();
    }
  };

  const handleBulkDeleteWallet = async () => {
    if (walletSelected.length === 0) return;
    if (!window.confirm(`Remove ${walletSelected.length} unused coupon(s)?`)) return;
    try {
      for (const id of walletSelected) {
        await customerCouponService.deleteCustomerCoupon(id);
      }
      addActivity(
        currentUser?.userId || 'Unknown',
        `Removed ${walletSelected.length} unused customer coupon(s)`
      );
      setWalletSelected([]);
      refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to remove coupons');
      refreshData();
    }
  };

  const handleDeleteWalletGroup = async (group, customerName) => {
    const count = group.entries.length;
    if (
      !window.confirm(
        count === 1
          ? `Remove unused coupon “${group.couponName}” for ${customerName || 'this customer'}?`
          : `Remove ${count} unused “${group.couponName}” coupons for ${customerName || 'this customer'}?`
      )
    ) {
      return;
    }
    try {
      for (const entry of group.entries) {
        await customerCouponService.deleteCustomerCoupon(entry.id);
      }
      addActivity(
        currentUser?.userId || 'Unknown',
        `Removed ${count} unused “${group.couponName}” coupon(s) for ${customerName || 'customer'}`
      );
      const removedIds = new Set(group.entries.map((e) => e.id));
      setWalletSelected((prev) => prev.filter((id) => !removedIds.has(id)));
      refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to remove coupon');
      refreshData();
    }
  };

  const openBillAdjustMenu = (event, entries) => {
    if (!entries?.length) return;
    if (entries.length === 1) {
      openBillAdjust(entries[0]);
      return;
    }
    const groups = groupEntriesByCouponType(entries);
    setBillMenuEntries(groups);
    setBillMenuAllEntries(entries);
    setBillMenuAnchor(event.currentTarget);
  };

  const closeBillAdjustMenu = () => {
    setBillMenuAnchor(null);
    setBillMenuEntries([]);
    setBillMenuAllEntries([]);
  };

  const openBillAdjust = (entry) => {
    setBillEntry(entry);
    const today = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    setBillDate(today);
    const outstanding = getOutstandingCredit(sales, entry.customerId, today);
    const maxAmt = roundMoney(
      Math.min(roundMoney(entry.remainingValue), Math.max(0, outstanding))
    );
    setBillAmount(String(maxAmt));
    setBillError(
      outstanding <= 0
        ? 'This customer has no outstanding credit bill for this month.'
        : maxAmt <= 0
          ? 'Nothing available to apply.'
          : ''
    );
    setBillOpen(true);
  };

  /**
   * Apply coupon wallet entries to credit bill (capped by outstanding).
   * @param {object[]} entries
   * @param {{ scopeLabel?: string }} [opts]
   */
  const applyEntriesToCreditBill = async (entries, opts = {}) => {
    closeBillAdjustMenu();
    if (!entries?.length || billSaving) return;

    const customerId = entries[0].customerId;
    const cust = customerById[customerId];
    if (!cust) {
      window.alert('Customer not found');
      return;
    }

    const today = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();

    // Re-read latest wallet balances so we don't overspend stale menu values
    const latestAll = await customerCouponService.getCustomerCoupons();
    const latestById = Object.fromEntries(latestAll.map((e) => [e.id, e]));
    const freshEntries = entries
      .map((e) => latestById[e.id])
      .filter((e) => e && roundMoney(e.remainingValue) > 0);

    if (freshEntries.length === 0) {
      window.alert('No unused coupon balance left. Refresh and try again.');
      refreshData();
      return;
    }

    const couponTotal = roundMoney(
      freshEntries.reduce((sum, e) => sum + roundMoney(e.remainingValue), 0)
    );
    const outstanding = getOutstandingCredit(sales, customerId, today);
    const amount = roundMoney(Math.min(couponTotal, Math.max(0, outstanding)));

    if (outstanding <= 0) {
      window.alert('This customer has no outstanding credit bill for this month.');
      return;
    }
    if (amount <= 0) {
      window.alert('Nothing to apply.');
      return;
    }

    const names = [
      ...new Set(freshEntries.map((e) => (e.couponName || 'Coupon').trim()).filter(Boolean)),
    ];
    const couponLabel =
      opts.scopeLabel ||
      (names.length === 1
        ? names[0]
        : names.length <= 2
          ? names.join(' + ')
          : `${names.length} coupons`);

    const scopeText = opts.scopeLabel
      ? `“${opts.scopeLabel}”`
      : 'all unused coupons';

    if (
      !window.confirm(
        `Apply Nu ${amount.toFixed(2)} from ${scopeText} for ${cust.name} to their credit bill?` +
          (amount < couponTotal
            ? `\n\n(Capped by outstanding credit Nu ${outstanding.toFixed(2)}; coupon remaining Nu ${couponTotal.toFixed(2)}.)`
            : '')
      )
    ) {
      return;
    }

    setBillSaving(true);
    try {
      const customerLabel = `${cust.name} (${cust.department || 'Other'})`;
      const note = creditBillCouponNote(couponLabel, amount);
      const ordered = [...freshEntries].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      );

      const allocations = await customerCouponService.redeemAcrossCustomerCoupons(
        ordered,
        amount
      );

      let couponSale = null;
      try {
        couponSale = await salesService.addSale({
          customerId: cust.id,
          customerName: customerLabel,
          date: today,
          paymentType: 'coupon',
          couponId: ordered[0]?.couponId || null,
          couponName: couponLabel,
          customerCouponId: allocations[0]?.id || ordered[0]?.id || null,
          couponAllocations: allocations,
          saleNote: note,
          items: [
            {
              mealId: null,
              mealName: `Coupon(s) applied to credit bill (${couponLabel})`,
              quantity: 1,
              unitPrice: amount,
              subtotal: amount,
            },
          ],
        });

        await salesService.addSale({
          customerId: cust.id,
          customerName: customerLabel,
          date: today,
          paymentType: 'credit',
          couponId: null,
          couponName: null,
          customerCouponId: null,
          saleNote: note,
          items: [
            {
              mealId: null,
              mealName: `Credit bill reduced by coupon(s) (${couponLabel})`,
              quantity: 1,
              unitPrice: -amount,
              subtotal: -amount,
            },
          ],
        });
      } catch (saleErr) {
        // Restore exact multi-wallet allocations first; then drop the coupon sale
        // without a second wallet restore.
        await customerCouponService.restoreAcrossCustomerCoupons(allocations).catch(() => {});
        if (couponSale?.id) {
          await salesService
            .deleteSale(couponSale.id, { skipWalletRestore: true })
            .catch(() => {});
        }
        throw saleErr;
      }

      addActivity(
        currentUser?.userId || 'Unknown',
        `Applied coupon Nu ${amount.toFixed(2)} (${couponLabel}) to credit bill for ${cust.name}`
      );
      await refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to apply coupon(s) to bill');
      await refreshData();
    } finally {
      setBillSaving(false);
    }
  };

  const handleBillAdjustAll = async (entries) => {
    await applyEntriesToCreditBill(entries);
  };

  const handleBillAdjust = async () => {
    if (!billEntry) return;
    const amount = roundMoney(billAmount);
    if (amount <= 0) {
      setBillError('Enter an amount greater than zero');
      return;
    }
    if (amount > roundMoney(billEntry.remainingValue) + 0.001) {
      setBillError(
        `Amount cannot exceed remaining Nu ${roundMoney(billEntry.remainingValue).toFixed(2)}`
      );
      return;
    }
    const cust = customerById[billEntry.customerId];
    if (!cust) {
      setBillError('Customer not found');
      return;
    }

    const outstanding = getOutstandingCredit(sales, billEntry.customerId, billDate);
    if (amount > outstanding + 0.001) {
      setBillError(
        `Amount cannot exceed this month’s outstanding credit (Nu ${outstanding.toFixed(2)})`
      );
      return;
    }
    if (outstanding <= 0) {
      setBillError('This customer has no outstanding credit bill for the selected month.');
      return;
    }

    setBillSaving(true);
    setBillError('');
    try {
      const customerLabel = `${cust.name} (${cust.department || 'Other'})`;
      const couponLabel = billEntry.couponName || 'Coupon';
      const note = creditBillCouponNote(couponLabel, amount);

      // Redeem first so a failed sale never leaves an unpaid coupon deduction gap
      await customerCouponService.redeemCustomerCoupon(billEntry.id, amount);

      let couponSale = null;
      try {
        // Coupon sale — shows under Coupon sales / reports
        couponSale = await salesService.addSale({
          customerId: cust.id,
          customerName: customerLabel,
          date: billDate,
          paymentType: 'coupon',
          couponId: billEntry.couponId,
          couponName: couponLabel,
          customerCouponId: billEntry.id,
          couponAllocations: [{ id: billEntry.id, amount }],
          saleNote: note,
          items: [
            {
              mealId: null,
              mealName: `Coupon applied to credit bill (${couponLabel})`,
              quantity: 1,
              unitPrice: amount,
              subtotal: amount,
            },
          ],
        });

        // Negative credit sale — reduces monthly credit bill
        await salesService.addSale({
          customerId: cust.id,
          customerName: customerLabel,
          date: billDate,
          paymentType: 'credit',
          couponId: null,
          couponName: null,
          customerCouponId: null,
          saleNote: note,
          items: [
            {
              mealId: null,
              mealName: `Credit bill reduced by coupon (${couponLabel})`,
              quantity: 1,
              unitPrice: -amount,
              subtotal: -amount,
            },
          ],
        });
      } catch (saleErr) {
        // Restore wallet first; delete coupon sale without a second restore.
        await customerCouponService
          .restoreCustomerCoupon(billEntry.id, amount)
          .catch(() => {});
        if (couponSale?.id) {
          await salesService
            .deleteSale(couponSale.id, { skipWalletRestore: true })
            .catch(() => {});
        }
        throw saleErr;
      }

      addActivity(
        currentUser?.userId || 'Unknown',
        `Applied coupon Nu ${amount.toFixed(2)} to credit bill for ${cust.name}`
      );
      refreshData();
      setBillOpen(false);
      setBillEntry(null);
    } catch (err) {
      setBillError(err.message || 'Failed to apply coupon to bill');
    } finally {
      setBillSaving(false);
    }
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          borderColor: couponsEnabled ? 'success.main' : 'divider',
          bgcolor: couponsEnabled ? 'action.hover' : 'background.paper',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Enable coupons for this canteen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {couponsEnabled
              ? 'On — coupon tabs, buttons, and tools are available in Sales and Reports.'
              : 'Off — all coupon tabs, buttons, and tools are hidden across the app for this tenant.'}
            {!canManageUsers ? ' Only managers can change this setting.' : ''}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ mr: 0, flexShrink: 0 }}
          control={
            <Switch
              color="success"
              checked={couponsEnabled}
              disabled={togglingFeature || !tenant?.id || !canManageUsers}
              onChange={(e) => handleToggleCouponsFeature(e.target.checked)}
            />
          }
          label={
            <Typography fontWeight={700}>
              {togglingFeature ? 'Saving…' : couponsEnabled ? 'Enabled' : 'Disabled'}
            </Typography>
          }
          labelPlacement="start"
        />
      </Paper>

      {!couponsEnabled ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Coupon features are disabled for this tenant. Turn the switch on to show coupon
            management here and coupon tools in Sales and Reports.
          </Typography>
        </Paper>
      ) : (
        <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40 }}>
          <Tab label="Coupon Types" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
          <Tab
            label="Customer Coupons"
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }}
          />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tab === 0 && selected.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleBulkDelete}
            >
              Delete selected ({selected.length})
            </Button>
          )}
          {tab === 1 && walletSelected.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleBulkDeleteWallet}
            >
              Remove selected ({walletSelected.length})
            </Button>
          )}
          {tab === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Coupon Type
            </Button>
          )}
        </Box>
      </Box>

      {tab === 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < coupons.length}
                    checked={coupons.length > 0 && selected.length === coupons.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Coupon Name</TableCell>
                <TableCell align="right">Qty / Coupon</TableCell>
                <TableCell align="right">Rate (Nu)</TableCell>
                <TableCell align="right">Value (Nu)</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No coupon types yet. Add one, then assign from Sales.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => {
                  const enabled = coupon.isActive !== false;
                  return (
                    <TableRow
                      key={coupon.id}
                      selected={selected.includes(coupon.id)}
                      sx={{ opacity: enabled ? 1 : 0.65 }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(coupon.id)}
                          onChange={() => handleSelectOne(coupon.id)}
                        />
                      </TableCell>
                      <TableCell>{coupon.name}</TableCell>
                      <TableCell align="right">{coupon.quantity}</TableCell>
                      <TableCell align="right">Nu {Number(coupon.rate).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        Nu {getCouponValue(coupon).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Chip
                            size="small"
                            label={enabled ? 'Enabled' : 'Disabled'}
                            color={enabled ? 'success' : 'default'}
                            variant={enabled ? 'filled' : 'outlined'}
                          />
                          <Switch
                            size="small"
                            checked={enabled}
                            disabled={togglingId === coupon.id}
                            onChange={() => handleToggleActive(coupon)}
                            inputProps={{ 'aria-label': `Toggle ${coupon.name}` }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpen(coupon)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(coupon.id, coupon.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Unused coupons held for each customer. Assign from Sales → Coupon Types. Use them on a meal
            sale in Sales, or apply here to reduce the customer’s monthly credit bill (amount and impact
            are shown before you confirm).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="wallet-dept-label">Department</InputLabel>
              <Select
                labelId="wallet-dept-label"
                label="Department"
                value={walletDept}
                onChange={(e) => setWalletDept(e.target.value)}
              >
                <MenuItem value="">
                  <em>All departments</em>
                </MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search customer or coupon..."
              value={walletSearch}
              onChange={(e) => setWalletSearch(e.target.value)}
              sx={{ minWidth: 240, flex: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        walletSelected.length > 0 && walletSelected.length < unusedWallet.length
                      }
                      checked={
                        unusedWallet.length > 0 && walletSelected.length === unusedWallet.length
                      }
                      onChange={handleWalletSelectAll}
                    />
                  </TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Coupons</TableCell>
                  <TableCell align="right">Remaining (Nu)</TableCell>
                  <TableCell align="right">Original (Nu)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {walletByCustomer.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No unused customer coupons. Assign from Sales → Coupon Types tab.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  walletByCustomer.map((row) => {
                    const cust = customerById[row.customerId];
                    const entryIds = row.entries.map((e) => e.id);
                    const selectedCount = entryIds.filter((id) =>
                      walletSelected.includes(id)
                    ).length;
                    const allSelected =
                      entryIds.length > 0 && selectedCount === entryIds.length;
                    const someSelected = selectedCount > 0 && !allSelected;
                    return (
                      <TableRow key={row.customerId || entryIds[0]} selected={allSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={someSelected}
                            checked={allSelected}
                            onChange={() => handleWalletSelectCustomer(entryIds)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{cust?.name || 'Unknown'}</Typography>
                          {row.entries.length > 1 && (
                            <Typography variant="caption" color="text.secondary">
                              {row.entries.length} coupon{row.entries.length === 1 ? '' : 's'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{cust?.department || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {groupEntriesByCouponType(row.entries).map((group) => (
                              <Chip
                                key={group.key}
                                size="small"
                                label={
                                  group.count > 1
                                    ? `${group.couponName} × ${group.count}`
                                    : group.couponName
                                }
                                onDelete={() =>
                                  handleDeleteWalletGroup(group, cust?.name || 'Unknown')
                                }
                                sx={{ maxWidth: '100%' }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Nu {row.remainingTotal.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          Nu {row.originalTotal.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={(e) => openBillAdjustMenu(e, row.entries)}
                          >
                            Apply to bill
                            {row.entries.length > 1 ? ' ▾' : ''}
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteCustomerWallets(row)}
                            title="Remove all unused coupons for this customer"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Menu
            anchorEl={billMenuAnchor}
            open={Boolean(billMenuAnchor)}
            onClose={closeBillAdjustMenu}
          >
            <MenuItem disabled dense>
              Choose coupon to apply
            </MenuItem>
            {billMenuAllEntries.length > 1 && (
              <MenuItem
                onClick={() => handleBillAdjustAll(billMenuAllEntries)}
                sx={{ fontWeight: 700 }}
              >
                Apply all — Nu{' '}
                {roundMoney(
                  billMenuAllEntries.reduce((sum, e) => sum + roundMoney(e.remainingValue), 0)
                ).toFixed(2)}{' '}
                total
              </MenuItem>
            )}
            {billMenuEntries.map((group) => {
              const ordered = [...(group.entries || [])].sort(
                (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
              );
              const first = ordered[0];
              if (!first) return null;
              return (
                <MenuItem
                  key={group.key}
                  onClick={() => {
                    if (ordered.length > 1) {
                      applyEntriesToCreditBill(ordered, {
                        scopeLabel: group.couponName || 'Coupon',
                      });
                    } else {
                      closeBillAdjustMenu();
                      openBillAdjust(first);
                    }
                  }}
                >
                  {group.count > 1
                    ? `${group.couponName} × ${group.count} — Nu ${roundMoney(
                        group.remainingValue
                      ).toFixed(2)}`
                    : `${group.couponName} — Nu ${roundMoney(group.remainingValue).toFixed(2)}`}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      )}
        </>
      )}

      {/* Add / Edit coupon type */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Coupon Type' : 'Add Coupon Type'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Coupon Name"
            fullWidth
            placeholder="e.g. Free Tea, Staff Meal Coupon"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Item Quantity per Coupon"
            type="number"
            fullWidth
            inputProps={{ min: 0.5, step: 0.5 }}
            helperText="How many free items this coupon covers"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Rate of Items (Nu)"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Unit rate of the free item(s)"
            value={form.rate}
            onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={form.isActive !== false}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
            }
            label={
              form.isActive !== false
                ? 'Enabled (available to assign & in Sales)'
                : 'Disabled (hidden in Sales)'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply to credit bill */}
      <Dialog open={billOpen} onClose={() => !billSaving && setBillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Coupon to Credit Bill</DialogTitle>
        <DialogContent>
          {billEntry && (() => {
            const cust = customerById[billEntry.customerId];
            const remaining = roundMoney(billEntry.remainingValue);
            const outstanding = getOutstandingCredit(sales, billEntry.customerId, billDate);
            const maxApply = roundMoney(Math.min(remaining, Math.max(0, outstanding)));
            const applyAmt = roundMoney(billAmount);
            const afterRemaining = roundMoney(Math.max(0, remaining - (applyAmt > 0 ? applyAmt : 0)));
            const validAmt =
              applyAmt > 0 &&
              applyAmt <= remaining + 0.001 &&
              applyAmt <= outstanding + 0.001;
            return (
              <Box sx={{ mb: 2 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {cust?.name || 'Customer'}
                    {cust?.department ? (
                      <Typography component="span" variant="body2" color="text.secondary" fontWeight={500}>
                        {' '}· {cust.department}
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Coupon: <strong>{billEntry.couponName || 'Coupon'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Coupon remaining: <strong>Nu {remaining.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credit outstanding (month): <strong>Nu {outstanding.toFixed(2)}</strong>
                  </Typography>
                </Paper>

                {billError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {billError}
                  </Alert>
                )}

                <TextField
                  margin="dense"
                  type="date"
                  label="Adjustment date"
                  fullWidth
                  value={billDate}
                  onChange={(e) => {
                    const next = e.target.value;
                    setBillDate(next);
                    if (!billEntry) return;
                    const outstanding = getOutstandingCredit(
                      sales,
                      billEntry.customerId,
                      next
                    );
                    const maxAmt = roundMoney(
                      Math.min(
                        roundMoney(billEntry.remainingValue),
                        Math.max(0, outstanding)
                      )
                    );
                    setBillAmount((prev) => {
                      const cur = roundMoney(prev);
                      return String(Math.min(cur > 0 ? cur : maxAmt, maxAmt));
                    });
                    setBillError(
                      outstanding <= 0
                        ? 'This customer has no outstanding credit bill for this month.'
                        : maxAmt <= 0
                          ? 'Nothing available to apply.'
                          : ''
                    );
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  margin="dense"
                  type="number"
                  label="Amount to deduct from credit bill (Nu)"
                  fullWidth
                  inputProps={{ min: 0.01, step: 0.01, max: maxApply || remaining }}
                  value={billAmount}
                  onChange={(e) => {
                    setBillAmount(e.target.value);
                    if (billError) setBillError('');
                  }}
                  helperText={`Max Nu ${maxApply.toFixed(2)} (min of coupon remaining and monthly credit)`}
                />

                {validAmt && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={700} gutterBottom>
                      What will be recorded
                    </Typography>
                    <Typography variant="body2" component="div">
                      · Coupon sale: <strong>Nu {applyAmt.toFixed(2)}</strong> ({billEntry.couponName})
                      <br />
                      · Credit bill reduced by: <strong>Nu {applyAmt.toFixed(2)}</strong>
                      <br />
                      · Coupon left after: <strong>Nu {afterRemaining.toFixed(2)}</strong>
                    </Typography>
                  </Alert>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillOpen(false)} disabled={billSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBillAdjust}
            disabled={
              billSaving ||
              !billEntry ||
              (() => {
                const remaining = roundMoney(billEntry.remainingValue);
                const outstanding = getOutstandingCredit(
                  sales,
                  billEntry.customerId,
                  billDate
                );
                const amt = roundMoney(billAmount);
                return (
                  amt <= 0 ||
                  outstanding <= 0 ||
                  amt > remaining + 0.001 ||
                  amt > outstanding + 0.001
                );
              })()
            }
          >
            {billSaving ? 'Applying…' : 'Apply to bill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Coupons;
